import {useTranslation} from 'react-i18next';
import React from 'react';
import ModalComponent from '@components/modal/Modal.component';
import {Button, Stack} from '@mui/material';
import NewTableComponent from '@components/table/NewTable.component';
import COLUMN_TYPES from '@utils/table/columnTypes.table';
import {GridCellParams, GridValueGetterParams} from '@mui/x-data-grid-pro';
import {ClientsService} from '@store/clients/services';

interface ExistingServicesModalProps {
  services: ClientsService[];
  handleClose: () => void;
  handleForce: () => void;
}

export const ExistingServicesModal = (props: ExistingServicesModalProps) => {
  const {handleClose, services, handleForce} = props;
  const {t} = useTranslation();

  return <ModalComponent
    aria-label="over-drawer"
    handleClose={handleClose}
    maxWidth="xl"
    title={t('clients.details.contractedServices.existing.title')}
    content={
      <Stack pt="20px" pb="20px" height="85vh" width="100%" direction="column" spacing={2} alignItems="center">
        <Stack flex={1} width="100%" spacing={2}>
          <NewTableComponent
            columns={[
              COLUMN_TYPES.TEXT({
                field: 'contract.contractName',
                label: t('clients.details.contracts.manage.name'),
                sortable: true,
                valueGetter: (params: GridValueGetterParams<any, ClientsService>) => params.row.contractName,
                width: 200,
              }),
              COLUMN_TYPES.TEXT({
                field: 'contract.contractReference',
                label: t('clients.details.contracts.manage.reference'),
                sortable: true,
                valueGetter: (params: GridValueGetterParams<any, ClientsService>) => params.row.contractReference,
                width: 200,
              }),
              COLUMN_TYPES.TEXT_BOLD({field: 'id', label: t('clients.servicesTable.id'), width: 100}),
              COLUMN_TYPES.TEXT_BOLD({
                field: 'service.clientWasteName',
                label: t('clients.servicesTable.name'),
                sortable: true,
                valueGetter: (params: GridValueGetterParams<any, ClientsService>) => params.row.clientWasteName,
                width: 350,
              }),
              COLUMN_TYPES.TEXT({
                field: 'schedulesCount',
                label: t('clients.servicesTable.frequency'),
                valueGetter: (params: GridValueGetterParams<any, ClientsService>) =>
                  (params.value ?? 0) > 0 ? t('global.frequencies.recurrent') : t('global.frequencies.onDemand'),
                width: 130,
              }),
              COLUMN_TYPES.TEXT({
                field: 'kind.kind',
                label: t('clients.servicesTable.type'),
                sortable: true,
                valueGetter: (params: GridValueGetterParams<any, ClientsService>) => params.row.kindName,
                width: 250,
              }),
              COLUMN_TYPES.TEXT({
                field: 'waste.name',
                label: t('clients.servicesTable.details'),
                sortable: true,
                valueGetter: (params: GridValueGetterParams<any, ClientsService>) => params.row.wasteName,
                width: 250,
              }),
              COLUMN_TYPES.TEXT({
                field: 'container.name',
                label: t('clients.servicesTable.container'),
                sortable: true,
                valueGetter: (params: GridValueGetterParams<any, ClientsService>) => params.row.containerName,
                width: 200,
              }),
              COLUMN_TYPES.TEXT({
                field: 'executionCount',
                label: t('execution.count'),
                sortable: true,
                width: 250,
              }),
              COLUMN_TYPES.TEXT({
                field: 'cOrg.name',
                label: t('clients.servicesTable.clientUnit'),
                sortable: true,
                valueGetter: (params: GridValueGetterParams<ClientsService, ClientsService>) => params.row.clientOrganisationUnitName,
                fullWidth: true,
                minWidth: 350,
              }),
              COLUMN_TYPES.TEXT({
                field: 'sOrg.name',
                label: t('clients.details.contractedServices.manage.supplierUnit'),
                sortable: true,
                valueGetter: (params: GridValueGetterParams<ClientsService, ClientsService>) => params.row.supplierOrganisationUnitName,
                fullWidth: true,
                minWidth: 350,
              }),
              COLUMN_TYPES.TEXT({
                field: 'buyPriceUnit',
                label: t('clients.servicesTable.purchasePrice'),
                valueGetter: (params: GridValueGetterParams<any, ClientsService>) => params.row.buyPriceIfOnlyOne !== null
                  ? `${params.row.buyPriceIfOnlyOne / 100} ${params.row.buyPriceCurrency || ''}`
                  : `${params.row.buyPricesCount || '-'}${params.row.buyPricesCount ? ' ' + t('suppliers.details.contractedServices.table.prices') : ''}`,
                width: 130,
              }),
              COLUMN_TYPES.TEXT({
                field: 'sellPriceUnit',
                label: t('clients.servicesTable.sellingPrice'),
                cellClassName: (params: GridCellParams<ClientsService>) => params.row.buyPriceIfOnlyOne && params.row.sellPriceIfOnlyOne && params.row.buyPriceIfOnlyOne > params.row.sellPriceIfOnlyOne ? 'error' : '',
                valueGetter: (params: GridValueGetterParams<any, ClientsService>) => params.row.sellPriceIfOnlyOne !== null
                  ? `${params.row.sellPriceIfOnlyOne / 100} ${params.row.sellPriceCurrency || ''}`
                  : `${params.row.sellPricesCount || '-'}${params.row.sellPricesCount ? ' ' + t('suppliers.details.contractedServices.table.prices') : ''}`,
                width: 130,
              }),
            ]}
            disableRowSelection
            paginationMode="client"
            disablePagination
            data={services}
          />
        </Stack>
        <Stack justifyContent="center" direction="row" spacing={2}>
          <Button variant="outlined" color="primary" onClick={handleClose}>
            {t('global.cancel')}
          </Button>
          <Button variant="contained" color="primary" onClick={() => handleForce()}>
            {t('clients.details.contracts.manage.forceContractCreation')}
          </Button>
        </Stack>
      </Stack>
    }
  />;
};

export default ExistingServicesModal;
